// Base airport properties
interface BaseAirport {
  id: number;
  name: string;
  code: string;
  lat: number;
  lng: number;
}

export interface AirportData extends BaseAirport {
  type: string;
  continent: string;
  iso_country: string;
  iso_region: string;
  municipality: string;
  gps_code: string;
  iata_code: string;
  local_code: string;
  latitude_deg: number;
  longitude_deg: number;
  airline: string;
  priority?: number;
}

export interface AirportSelection extends BaseAirport {
  count?: number;
  municipality?: string;
  type?: string;
  full_name?: string;
  iso_country?: string;
}

// Base flight information
interface BaseFlightInfo {
  price: number;
  average_duration: number;
  max_duration: number;
  median_duration: number;
  url: string;
}

// Optional flight metadata
interface FlightMetadata {
  flights?: FlightSegment[] | Record<string, unknown> | null;
  isManual?: boolean;
  numberOfLayovers?: number;
  layoverTime?: number;
  isLoading?: boolean;
  carbon_emissions?: number;
  carbon_emissions_difference?: number;
  search_id?: string | null;
  serpapi_raw_response?: Record<string, unknown> | null;
  total_trip_duration?: number;
  main_airline?: string | null;
  main_flight_number?: string | null;
  main_airline_logo?: string | null;
}

export interface FlightPairInfo extends BaseFlightInfo, FlightMetadata {}

// Airport time information
interface AirportTimeInfo {
  code?: string;
  airport?: string;
  time?: string;
}

export interface FlightSegment {
  departure?: AirportTimeInfo;
  arrival?: AirportTimeInfo;
  departure_airport?: AirportTimeInfo;
  arrival_airport?: AirportTimeInfo;
  airline?: string;
  flight_number?: string;
  duration?: number;
  segment_number?: number;
}

export interface Layover {
  duration?: number;
  name?: string;
  id?: string;
}

export interface FlightPair {
  dep: AirportSelection;
  arr: AirportSelection;
}

export interface DateTimeRange {
  start_date: Date;
  start_time?: string;
  end_date: Date;
  end_time?: string;
}

export interface Group {
  group_name: string;
  group_travel_class: string;
  arrival_datetime_range?: DateTimeRange; // arrival to location
  departure_datetime_range?: DateTimeRange; // return to origin
}

export interface AirlineData {
  iata_code: string;
  name: string;
}

export interface AdvancedFlightSettings {
  specific_airlines: AirlineData[];
  exclude_airlines: AirlineData[];
  prioritize: PrioritizeOption;
  include_bag_costs: boolean;
  deep_search: boolean;
  flight_type: FlightType;
  stops: StopOption;
}

export const TRAVEL_CLASS_OPTIONS = [
  { value: "1", name: "economy", label: "Economy" },
  { value: "2", name: "premium economy", label: "Premium Economy" },
  { value: "3", name: "business", label: "Business" },
  { value: "4", name: "first", label: "First" },
] as const;

export type TravelClass = (typeof TRAVEL_CLASS_OPTIONS)[number]["name"];

// Sort by parameter mapping for flight prioritization
// 1 - Top flights (default), 2 - Price, 3 - Departure time, 4 - Arrival time, 5 - Duration, 6 - Emissions
export const PRIORITIZE_OPTIONS = [
  { value: "top_flights", label: "Top Flights", sortBy: 1 },
  { value: "lowest", label: "Lowest Price", sortBy: 2 },
  { value: "departure_time", label: "Departure Time", sortBy: 3 },
  { value: "arrival_time", label: "Arrival Time", sortBy: 4 },
  { value: "shortest", label: "Shortest Flight Duration", sortBy: 5 },
  { value: "emissions", label: "Less Carbon Emissions", sortBy: 6 },
] as const;

export type PrioritizeOption = (typeof PRIORITIZE_OPTIONS)[number]["value"];
export const DEFAULT_PRIORITIZE_OPTION: PrioritizeOption = "top_flights";

// Helper to get sort_by value from prioritize value
export const getSortByFromPrioritize = (prioritize: PrioritizeOption): number => {
  return PRIORITIZE_OPTIONS.find((opt) => opt.value === prioritize)!.sortBy;
};

export const FLIGHT_TYPES = [
  { value: "1", label: "Round Trip" },
  { value: "2", label: "One Way" },
  { value: "3", label: "Multi-city" },
] as const;

export type FlightType = (typeof FLIGHT_TYPES)[number]["value"];

export const STOP_OPTIONS = [
  { value: 0, label: "Any number of stops" },
  { value: 1, label: "Nonstop" },
  { value: 2, label: "1 stop or fewer" },
  { value: 3, label: "2 stops or fewer" },
] as const;

export type StopOption = (typeof STOP_OPTIONS)[number]["value"];

export interface DepartureGroup {
  group_name: string;
  count: number;
}

export interface Departure {
  id: number;
  code: string;
  name: string;
  lat: number;
  lng: number;
  municipality?: string;
  type?: string;
  iso_country?: string;
  groups: DepartureGroup[];
}

export interface FlightEstimate {
  id: string;
  arrivalAirport: AirportData;
  totalEstimatedCost: number;
  hasError?: boolean;
  isFavorite: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Base group flight cost (required fields)
interface BaseGroupFlightCost {
  groupName: string;
  flightClass: string;
  travelerCount: number;
  numberOfLayovers: number;
  layoverTime: number; // in minutes
  cost: number;
}

// Optional group flight cost fields
interface GroupFlightCostOptional {
  flightTime?: number; // in minutes
  carbonEmissions?: number;
  url?: string; // Google Flights URL
  error?: string; // Error message if flight has error
  airline?: string; // Main airline name
  airlineLogo?: string; // Airline logo URL
  flightNumber?: string; // Main flight number
}

export interface GroupFlightCost extends BaseGroupFlightCost, GroupFlightCostOptional {}

export interface DepartureFlightCosts {
  departureAirport: AirportData;
  totalTravelers: number;
  totalCost: number;
  groups: GroupFlightCost[];
  avgFlightTime?: number; // in minutes
  carbonEmissions?: number;
}

export interface DetailedFlightCosts {
  arrivalAirport: AirportData; // single arrival destination
  totalEstimatedCost: number;
  totalTravelers: number;
  departureCosts: DepartureFlightCosts[]; // costs grouped by departure airport
  avgFlightTime?: number; // in minutes - average for everyone
  totalCarbonEmissions?: number;
  totalNumberOfLayovers?: number;
}

// DB insert types for flight search operations

export interface FlightSearchGroupInsert {
  flight_search_id: number;
  group_name: string;
  departure_code: string;
  traveler_count: number;
  travel_class: string;
  arrival_start_date: string | null;
  arrival_end_date: string | null;
  arrival_start_time: string | null;
  arrival_end_time: string | null;
  return_start_date: string | null;
  return_end_date: string | null;
  return_start_time: string | null;
  return_end_time: string | null;
}

export interface FlightSearchDestinationInsert {
  flight_search_id: number;
  arrival_code: string;
}

export interface FlightSearchResultInsert {
  flight_search_id: number;
  group_id: number;
  arrival_code: string;
  travelers: number;
  price_per_traveler: number;
  total_price: number;
  layovers_per_one_way: number;
  total_layovers_per_one_way: number;
  layover_time_one_way_minutes: number;
  avg_segment_duration_one_way_minutes: number;
  median_segment_duration_one_way_minutes: number;
  longest_segment_duration_one_way_minutes: number;
  total_trip_duration_one_way_minutes: number;
  departure_time_local: string | null;
  arrival_time_local: string | null;
  main_airline: string | null;
  main_flight_number: string | null;
  main_airline_logo: string | null;
  carbon_emissions_kg: number | null;
  carbon_emissions_percent_diff: number | null;
  num_segments_one_way: number;
  google_flights_url: string;
  is_favorite: boolean;
  raw_api_response: Record<string, unknown> | null;
}

export interface FlightSearchResultSegmentInsert {
  result_id: number;
  segment_number: number;
  airline: string | null;
  flight_number: string | null;
  duration_minutes: number | null;
}
